import type { CartEntry } from "@larder/shared";

/**
 * One line on the shopping list.
 *
 * Two jobs, and the row splits along the line between them. Ticking the circle
 * says "I have this now" — the tap you make in a shop with one hand full, so it
 * keeps its own target and its own place on the left, where a shopping list has
 * put its checkbox since long before there were phones. Tapping the NAME opens
 * the line, which is where buying it actually happens now: four shops with the
 * search already typed, and what it cost last time. See CartDetail.
 *
 * The row used to spend its whole width on the tick, and opening a line went to
 * the add form — asking for an expiry date and a shelf for something still
 * sitting in a shop.
 *
 * Removing moved INTO the line, and the small ✕ that used to sit here is gone.
 * Two glyphs at the end of a row — a chevron saying "opens" beside a cross
 * saying "deletes" — is a choice nobody should have to make at arm's length in
 * a shop, and changing your mind is the rarest thing that happens to a list.
 * It is now a labelled button one tap in, which is also a great deal harder to
 * hit by accident.
 *
 * `reason` is shown, quietly. "You ran out of this on Tuesday" is the sentence
 * that makes the list trustworthy rather than a pile someone has to audit.
 */
export function CartRow({
  entry,
  onBuy,
  onOpen,
  busy,
  index = 0,
}: {
  entry: CartEntry;
  onBuy: () => void;
  onOpen: () => void;
  busy: boolean;
  index?: number;
}) {
  return (
    <div
      className="cart-row"
      style={{ animationDelay: `${Math.min(index, 6) * 45}ms` }}
      data-testid={`cart-${entry.name}`}
    >
      <button
        type="button"
        className="cart-tick"
        disabled={busy}
        onClick={onBuy}
        aria-label={`Got ${entry.name}`}
        data-testid={`cart-buy-${entry.name}`}
      >
        <span aria-hidden="true">○</span>
      </button>
      <button
        type="button"
        className="cart-main"
        disabled={busy}
        onClick={onOpen}
        data-testid={`cart-open-${entry.name}`}
      >
        <span className="item-main">
          <span className="item-name">
            {entry.name}
            {entry.qty > 1 && <span className="dupe">×{entry.qty}</span>}
          </span>
          <span className="item-meta">
            {[entry.sizeText, WHY[entry.reason]].filter(Boolean).join(" · ")}
          </span>
        </span>
        {/* Says the line goes somewhere without spending a word saying so. */}
        <span className="chev" aria-hidden="true">
          ›
        </span>
      </button>
    </div>
  );
}

/** Why it is on the list, in the words a person would use. */
const WHY: Record<CartEntry["reason"], string> = {
  ran_out: "you ran out",
  search_miss: "you looked for it",
  buy_again: "you wanted it again",
  manual: "",
};
